/**
 * 判断两线角尺寸是否放置在补角象限。
 *
 * 象限编号由智能尺寸放置解析器持久化：0/2 表示两组对顶主角，1/3 表示
 * 两组对顶补角。未记录象限的旧尺寸延续既有主角语义，避免加载后静默换支。
 *
 * @param angleQuadrant 两线角放置象限。
 * @returns 是否使用补角业务语义。
 */
function isSupplementaryAngleQuadrant(angleQuadrant: 0 | 1 | 2 | 3 | undefined): boolean {
    // 两组对顶补角共享同一业务换算，象限方向由 directionSign 另行保持。
    return angleQuadrant === 1 || angleQuadrant === 3;
}

/**
 * 把两线角尺寸的业务名义值转换为求解器需要约束的几何夹角。
 *
 * `valueScale` 只承担对称完整角等业务倍率，因此必须先还原选中角域的角度；
 * 补角象限显示的是 `π-几何夹角`，求解目标需要再反算回几何夹角。顺序反转
 * 会让完整对称角或其它非 1 倍业务值落到错误分支。
 *
 * @param businessValue 尺寸业务名义值（弧度）。
 * @param valueScale 业务值相对选中角域的换算系数。
 * @param angleQuadrant 两线角放置象限。
 * @returns 求解器需要约束的几何夹角（弧度）。
 */
export function resolveSketchDimensionAngleGeometricValue(
    businessValue: number,
    valueScale: number,
    angleQuadrant: 0 | 1 | 2 | 3 | undefined,
): number {
    // valueScale 描述业务显示倍率，不能参与 π 补角本身的几何计算。
    const selectedZoneAngle = businessValue / valueScale;

    // 补角业务值需要反算回两条实体有向线实际形成的主夹角。
    return isSupplementaryAngleQuadrant(angleQuadrant)
        ? Math.PI - selectedZoneAngle
        : selectedZoneAngle;
}

/**
 * 把当前两线几何夹角转换为指定放置象限的尺寸业务测量值。
 *
 * driven 重测必须与 driving 编译使用同一象限契约：先从几何主夹角解析
 * 用户放置的主角或补角，再应用对称完整角等业务倍率。
 *
 * @param geometricValue 当前两线几何夹角幅值（弧度，范围 0 到 π）。
 * @param valueScale 选中角域到业务值的换算系数。
 * @param angleQuadrant 两线角放置象限。
 * @returns 指定象限对应的业务测量值（弧度）。
 */
export function resolveSketchDimensionAngleBusinessValue(
    geometricValue: number,
    valueScale: number,
    angleQuadrant: 0 | 1 | 2 | 3 | undefined,
): number {
    // 先保留用户放置时选择的角域，再交给统一业务倍率生成最终显示值。
    const selectedZoneAngle = isSupplementaryAngleQuadrant(angleQuadrant)
        ? Math.PI - geometricValue
        : geometricValue;

    // 完整对称角等业务模式只放大已选角域，不允许改变求解分支。
    return selectedZoneAngle * valueScale;
}
